import { useEffect, useRef, useState } from "react";
import { LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { HiOutlinePlus } from "react-icons/hi";
import ErrorModal from "../../common/CustomModel";
import ConfirmDeleteModal from "../../common/DeleteModel";
import FormFields from "../../common/FormFields";
import Model from "../../common/Model";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import { MenuMasterAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import { RegistryHeader, Button as SharedButton } from "../../common/Shared";

const MenuMaster = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorSubMessage, setErrorSubMessage] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(false);

    // Menu Registry State
    const [menuData, setMenuData] = useState([]);
    const [menuFormData, setMenuFormData] = useState({ menu_ID: 0, menu_Name: "", displayOrder: 0 });

    // Pagination & Scroll
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableScrollRef = useRef(null);
    const isFetchingRef = useRef(false);
    const pageSize = 50;

    const fetchData = async () => {
        if (isFetchingRef.current) return;
        setLoading(true);
        try {
            const res = await MenuMasterAPI.getAllPagination(currentPage, pageSize);
            const items = res.data;
            setMenuData(currentPage === 1 ? items : [...menuData, ...items]);
            setHasMore(items.length >= pageSize);
        } catch (error) {
            notify("Error fetching data", "error");
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const onScroll = () => {
            if (loading || !hasMore || isFetchingRef.current) return;
            const { scrollTop, clientHeight, scrollHeight } = el;
            if (scrollHeight - scrollTop <= clientHeight + 100) {
                isFetchingRef.current = true;
                setCurrentPage(p => p + 1);
            }
        };
        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, [loading, hasMore]);

    const handleAdd = () => {
        setIsEditing(false);
        setMenuFormData({ menu_ID: 0, menu_Name: "", displayOrder: "" });
        setIsModelOpen(true);
    };

    const handleEdit = async (id) => {
        setIsEditing(true);
        setLoading(true);
        try {
            const data = await MenuMasterAPI.getById(id);
            setMenuFormData({ menu_ID: data.menu_ID, menu_Name: data.menu_Name, displayOrder: data.displayOrder });
            setIsModelOpen(true);
        } catch (error) {
            notify("Fetch failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            await MenuMasterAPI.saveOrUpdate(menuFormData);
            notify("Saved successfully", "success");
            setCurrentPage(1);
            fetchData();
            setIsModelOpen(false);
        } catch (error) {
            notify("Operation failed", "error");
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await MenuMasterAPI.delete(selectedId);
            notify("Deleted successfully", "success");
            setCurrentPage(1);
            fetchData();
        } catch (error) {
            notify("Delete failed", "error");
        }
        setIsDeleteModalOpen(false);
        setSelectedId(null);
    };

    const filteredRegistry = menuData.filter(d => d.menu_Name?.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <>
            <div className="flex flex-col h-full bg-white font-plus overflow-hidden">
                <RegistryHeader title="Menu Management">
                    <div className="relative w-full sm:w-80 group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-primary">
                            <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search..."
                            className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-primary focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <MdClose size={18} />
                            </button>
                        )}
                    </div>

                    <SharedButton
                        onClick={handleAdd}
                        icon={HiOutlinePlus}
                    >
                        Add Menu
                    </SharedButton>
                </RegistryHeader>

                <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
                    <div ref={tableScrollRef} className="relative overflow-auto border border-gray-200 bg-white flex-1">
                        {loading && <Loader centered />}
                        <div className="min-w-max">
                            <Table
                                columns={[
                                    { key: "displayOrder", label: "Display Order" },
                                    { key: "menu_Name", label: "Menu Name" },
                                ]}
                                data={filteredRegistry}
                                onEdit={(row) => handleEdit(row.menu_ID)}
                                onDelete={(row) => {
                                    setSelectedId(row.menu_ID);
                                    setIsDeleteModalOpen(true);
                                }}
                                tableClassName="text-[12px] font-bold"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Model
                isOpen={isModelOpen}
                onClose={() => setIsModelOpen(false)}
                name={isEditing ? "Edit Menu" : "Create Menu"}
                className="max-w-xl"
            >
                <FormFields
                    onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                    setIsModelOpen={setIsModelOpen}
                    formData={menuFormData}
                    handleInputChange={(e) => setMenuFormData(prev => ({ ...prev, [e.target.name]: e.target.type === "number" ? Number(e.target.value) : e.target.value }))}
                    fields={[
                        { name: "menu_Name", label: "Menu Name", type: "text", required: true },
                        { name: "displayOrder", label: "Display Order", type: "number" },
                    ]}
                    isEditing={isEditing}
                    onClear={() => setIsModelOpen(false)}
                />
            </Model>

            <ConfirmDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
            />

            {errorModalOpen && (
                <ErrorModal
                    message={errorMessage}
                    subMessage={errorSubMessage}
                    onClose={() => setErrorModalOpen(false)}
                />
            )}
        </>
    );
};

export default MenuMaster;
